<script setup lang="ts">
    import { computed } from 'vue';
    import { useHipSpineXRaySceneViewModel } from '@/views/3d-planner/hipSpine/useHipSpineXRaySceneViewModel';
    import HipSpineAnnotation from './HipSpineAnnotation.vue';
    import type {
        AnnotationGroup,
        HipSpineAnnotationCategory,
    } from '@/views/3d-planner/hipSpine/types';

    defineProps<{
        posture: string;
    }>();

    const sceneVm = useHipSpineXRaySceneViewModel();
    const groups = computed(() => sceneVm.sceneState.nodes.annotationGroups);

    type AnnotationSection = {
        category: HipSpineAnnotationCategory;
        displayName: string;
        colour: string;
        groups: AnnotationGroup[];
    };

    const sections = computed<AnnotationSection[]>(() => {
        const map = new Map<HipSpineAnnotationCategory, AnnotationSection>();
        for (const g of groups.value) {
            if (!map.has(g.category)) {
                map.set(g.category, {
                    category: g.category,
                    displayName: g.category.charAt(0).toUpperCase() + g.category.slice(1),
                    colour: g.colour,
                    groups: [],
                });
            }
            map.get(g.category)!.groups.push(g);
        }
        return [...map.values()];
    });
</script>

<template>
    <div class="landmark-annotations" data-testid="hip-spine-annotations">
        <HipSpineAnnotation
            v-for="section in sections"
            :key="section.category"
            :posture="posture"
            :category="section.displayName"
            :category-key="section.category"
            :colour="section.colour"
            :groups="section.groups"
        />
    </div>
</template>

<style scoped lang="scss">
    .landmark-annotations {
        padding-bottom: 8px;
        padding-top: 0;
        overflow-y: auto;

        &-empty {
            text-align: center;
            color: rgba(var(--v-theme-secondary-text));
            font-size: 13px;
            padding: 16px;
        }
    }

    .landmark-annotations > * {
        margin: 10px 0;
    }
</style>
